/* Keep filter controls interactive while Shopify renders the next result set. */
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

const jobs = new Map();
const installed = new WeakSet();
const loadingSections = new WeakSet();
function status(section, text, busy = false, retry) {
  const wasBusy = loadingSections.has(section);
  if (busy) loadingSections.add(section);
  else loadingSections.delete(section);
  section.toggleAttribute('data-filters-loading', busy);
  const grid = section.querySelector('.product-grid');
  if (grid) {
    grid.inert = busy;
    if (busy) grid.getAnimations().forEach(animation => animation.cancel());
    else if (wasBusy && !retry && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      grid.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    }
  }
  section.querySelector('results-list')?.setAttribute('aria-busy', String(busy));
  let node = section.querySelector('.nv-filter-status');
  if (!node) {
    node = document.createElement('div');
    node.className = 'nv-filter-status';
    node.setAttribute('role', 'status');
    section.querySelector('.olaola-filters__form')?.append(node);
  }
  node.textContent = text;
  node.classList.toggle('nv-filter-status--error', !!retry);
  if (retry) {
    section.querySelector('.is-open .olaola-filters__dropdown-content')?.append(node);
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Zkusit znovu';
    button.addEventListener('click', retry, { once: true });
    node.append(' ', button);
  }
}
function update(form, url, retryFresh = false, pushHistory = true) {
  const section = form.closest('.shopify-section');
  if (!section) return;
  const key = form.sectionId;
  const previous = jobs.get(key);
  clearTimeout(previous?.timer);
  const job = { timer: null };
  jobs.set(key, job);
  status(section, 'Aktualizuji produkty…', true);
  job.timer = setTimeout(async () => {
    try {
      let html;
      if (retryFresh) {
        const request = new URL(url);
        request.searchParams.set('section_id', key.replace(/^shopify-section-/, ''));
        const response = await fetch(request);
        if (!response.ok) throw new Error('Section request failed');
        html = await response.text();
      } else {
        html = await sectionRenderer.getSectionHTML(key, true, new URL(url));
      }
      if (jobs.get(key) !== job) return;
      const open = section.querySelector('[data-filter-dropdown-trigger][aria-expanded="true"]');
      const openWrapper = open?.closest('[data-filter-dropdown]');
      const openKey = openWrapper?.getAttribute('data-filter-param-name');
      const dropdownScroll = openWrapper?.querySelector('.olaola-filters__dropdown-content')?.scrollTop || 0;
      const badgeScroll = section.querySelector('.olaola-filters__badges')?.scrollLeft || 0;
      const focusId = section.contains(document.activeElement) ? document.activeElement.id : '';
      await morphSection(key, html);
      install(section);
      if (pushHistory && location.href !== url) history.pushState({ urlParameters: new URL(url).searchParams.toString() }, '', url);
      form.dispatchEvent(new FilterUpdateEvent(new URL(url).searchParams));
      const wrapper = openKey && Array.from(section.querySelectorAll('[data-filter-dropdown]')).find(el => el.getAttribute('data-filter-param-name') === openKey);
      if (wrapper) {
        wrapper.querySelector('[data-filter-dropdown-trigger]')?.setAttribute('aria-expanded', 'true');
        const dropdown = wrapper.querySelector('[data-filter-dropdown-content]');
        dropdown?.classList.add('is-open');
        if (dropdown) { dropdown.style.marginLeft = '0px'; const bounds = dropdown.getBoundingClientRect(); dropdown.style.marginLeft = Math.max(16 - bounds.left, Math.min(0, innerWidth - 16 - bounds.right)) + 'px'; }
        const content = wrapper.querySelector('.olaola-filters__dropdown-content');
        if (content) content.scrollTop = dropdownScroll;
        document.querySelector('[data-filter-overlay]')?.classList.add('is-visible');
      }
      const badges = section.querySelector('.olaola-filters__badges');
      if (badges) badges.scrollLeft = badgeScroll;
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      status(section, '');
      jobs.delete(key);
    } catch (error) {
      if (jobs.get(key) !== job) return;
      status(section, 'Produkty se nepodařilo aktualizovat.', false, () => update(form, url, true, pushHistory));
      jobs.delete(key);
      console.error('Filter update failed', error);
    }
  }, 120);
}
function install(root = document) {
  root.querySelectorAll('facets-form-component').forEach(form => {
    if (installed.has(form) || !form.querySelector('.olaola-filters__form, .olaola-filters__bottom-sheet-inner')) return;
    installed.add(form);
    form.updateFilters = () => {
      const url = new URL(location.href);
      url.search = form.createURLParameters().toString();
      update(form, url.href);
    };
    form.updateFiltersByURL = url => update(form, new URL(url, location.href).href);
  });
}
await customElements.whenDefined('facets-form-component');
install();
// Reinstall only after section rendering, rather than scanning every DOM mutation.
document.addEventListener('shopify:section:load', event => install(event.target));
// A pending response must not override browser history navigation.
window.addEventListener('popstate', () => {
  jobs.forEach(job => clearTimeout(job.timer));
  jobs.clear();
  document.querySelectorAll('[data-filters-loading]').forEach(section => status(section, ''));
  const sections = new Set();
  document.querySelectorAll('facets-form-component').forEach(form => {
    if (!installed.has(form) || sections.has(form.sectionId)) return;
    sections.add(form.sectionId);
    update(form, location.href, false, false);
  });
});
